import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { brandInfo } from "@/mocks/brand";
import { useTranslation } from "react-i18next";

export default function About() {
  const { t } = useTranslation("about");
  const { t: tc } = useTranslation("common");
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hpValue = (formData.get("company_alt") as string || "").trim();
    if (hpValue) {
      setFormStatus("success");
      setFormName("");
      setFormEmail("");
      setFormMessage("");
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append("name", formData.get("name") as string);
      body.append("email", formData.get("email") as string);
      body.append("message", formData.get("message") as string);

      const res = await fetch("https://readdy.ai/api/form/d9qtebknlsngrm4lm5pg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const code = (parsed?.code as string) || "";

      if (res.ok && code === "OK") {
        setFormStatus("success");
        setFormName("");
        setFormEmail("");
        setFormMessage("");
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || (parsed?.message as string)
          || "";
        setFormError(serverMsg || t("formError"));
        setFormStatus("error");
      }
    } catch {
      setFormError(t("formError"));
      setFormStatus("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: "关于我们 - 华丽的外出",
          description: "华丽的外出品牌介绍、口碑评价与联系方式",
          url: "https://www.hualidewaichu.com/about",
          inLanguage: "zh-CN",
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.hualidewaichu.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "关于我们",
            },
          ],
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: t("breadcrumbHome"), href: "/" },
                { label: t("breadcrumbAbout") },
              ]}
            />

            <section className="mb-16">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
                {t("title")}
              </h1>
              <p className="text-sm text-foreground-500 mb-8">{t("subtitle")}</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-800 mb-4">
                    {t("brandIntro")}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {t("brandDesc")}
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t("brandMission")}
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden h-72 lg:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20fashion%20studio%20interior%20with%20clothing%20racks%20and%20warm%20lighting%2C%20creative%20workspace%2C%20cream%20and%20beige%20color%20palette%2C%20sophisticated%20editorial%20interior%20photography%2C%20soft%20natural%20light%20through%20windows&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="华丽的外出品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            <section className="mb-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                    {brandInfo.stats.articles.toLocaleString()}+
                  </div>
                  <p className="text-xs text-foreground-500">精选文章</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-2">
                    {(brandInfo.stats.readers / 10000).toFixed(0)}万+
                  </div>
                  <p className="text-xs text-foreground-500">忠实读者</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                    {brandInfo.stats.categories}
                  </div>
                  <p className="text-xs text-foreground-500">内容分类</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-2">
                    {brandInfo.stats.years}+
                  </div>
                  <p className="text-xs text-foreground-500">年深耕</p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-8 text-center">
                {t("milestones")}
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {brandInfo.milestones.map((m, i) => (
                    <div key={m.year} className={`relative flex items-start gap-6 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                      <div className="hidden md:block w-1/2"></div>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-accent-500 rounded-full -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className="ml-10 md:ml-0 md:w-1/2 bg-background-100 rounded-lg p-4 md:p-5">
                        <span className="text-xs font-bold text-accent-500 mb-1 block">{m.year}</span>
                        <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-1">{m.title}</h3>
                        <p className="text-xs text-foreground-500">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2 text-center">
                {t("reputationTitle")}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">{t("reputationSubtitle")}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {brandInfo.testimonials.map((tm, i) => (
                  <div key={i} className="bg-background-100 rounded-lg p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img src={tm.avatar} alt={tm.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{tm.name}</p>
                        <p className="text-xs text-foreground-500">{tm.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{tm.content}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16" id="contact">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2 text-center">
                {t("contactTitle")}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">{t("contactSubtitle")}</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t("email")}</p>
                      <p className="text-sm text-foreground-500">{brandInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t("wechat")}</p>
                      <p className="text-sm text-foreground-500">{brandInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0">
                      <i className="ri-reddit-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t("xiaohongshu")}</p>
                      <p className="text-sm text-foreground-500">{brandInfo.xiaohongshu}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t("workingHours")}</p>
                      <p className="text-sm text-foreground-500">{brandInfo.workingHours}</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form="" className="bg-background-100 rounded-lg p-5 md:p-6 space-y-4">
                  <div className="hp-field-wrapper">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t("formName")}
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formName}
                      onChange={(e) => { setFormName(e.target.value); setFormStatus("idle"); }}
                      placeholder={t("formNamePlaceholder")}
                      required
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t("formEmail")}
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formEmail}
                      onChange={(e) => { setFormEmail(e.target.value); setFormStatus("idle"); }}
                      placeholder={t("formEmailPlaceholder")}
                      required
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t("formMessage")}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formMessage}
                      onChange={(e) => { setFormMessage(e.target.value); setFormStatus("idle"); }}
                      placeholder={t("formMessagePlaceholder")}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formMessage.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t("formSubmit")}
                  </button>
                  {formStatus === "success" && (
                    <p className="text-sm text-accent-600 text-center">{t("formSuccess")}</p>
                  )}
                  {formStatus === "error" && (
                    <p className="text-sm text-accent-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}